import { badgeCount } from "@bundle/core/logic/format";
import { usePrimitiveT } from "../i18n/I18nProvider.tsx";
import type { ChipTagTone, ChipTone } from "./Chip.tsx";
import { color, cx, type ColorToken } from "./tokens.ts";

/** `tag.<color>`: an option or mail tab colour, as `tag.<color>.bg` / `.fg` (the Chip pair). */
export type BadgeTone = "danger" | "accent" | "neutral" | ChipTagTone;

export type BadgeProps = {
  count?: number;
  dot?: boolean;
  tone?: BadgeTone;
  /**
   * What assistive tech reads (translated). Default for a count: "3 unread" (the real count, also past 99+). A dot is
   * hidden from assistive tech unless it has a label.
   */
  accessibilityLabel?: string;
};

/** A chip's tone as a badge tone (a mail tab's count beside its "N new" chip): tags and the solid three; others neutral. */
export function badgeTone(tone: ChipTone): BadgeTone {
  return tone === "accent" || tone === "danger" || tone === "neutral" || tone.startsWith("tag.") ? (tone as BadgeTone) : "neutral";
}

function toneProps(tone: BadgeTone): { className: string | undefined; style: { background: string; color: string } | undefined } {
  if (!tone.startsWith("tag.")) return { className: `b-tone-${tone}`, style: undefined };
  return { className: undefined, style: { background: color(`${tone}.bg` as ColorToken), color: color(`${tone}.fg` as ColorToken) } };
}

/** Count capsule ("99+") or a dot. Renders nothing for a zero count without `dot`. */
export function Badge({ count, dot, tone = "danger", accessibilityLabel }: BadgeProps) {
  const t = usePrimitiveT();
  const { className, style } = toneProps(tone);
  if (dot === true) {
    if (accessibilityLabel === undefined) return <span className={cx("b-badge-dot", className)} style={style} aria-hidden />;
    return (
      <span className={cx("b-badge-dot", className)} style={style}>
        <span className="b-visually-hidden">{accessibilityLabel}</span>
      </span>
    );
  }
  const text = badgeCount(count ?? 0);
  if (text === "") return null;
  return (
    <span className={cx("b-badge", className)} style={style}>
      <span aria-hidden>{text}</span>
      <span className="b-visually-hidden">{accessibilityLabel ?? t("primitive.badge.unread", { count: count ?? 0 })}</span>
    </span>
  );
}
